import React, { useState } from 'react';
import { TagihanSiswa, Siswa, PosTagihan, Kelas } from '../../types/index.js';
import { Plus, Search, Filter, Edit, Trash2, Calendar, CheckCircle2, AlertCircle, Zap, FileText, Save, CheckCircle, RefreshCcw, LogOut, ArrowRightCircle } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { api } from '../../services/api.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';
import { SearchableSelect } from '../ui/SearchableSelect.js';

interface TagihanSiswaViewProps {
  tagihanList: TagihanSiswa[];
  siswaList: Siswa[];
  posList: PosTagihan[];
  kelasList: Kelas[];
  onGenerateTagihan: (payload: any) => Promise<void>;
  onDeleteTagihan: (id: string) => Promise<void>;
  onNavigateToRecurring?: () => void;
}

export const TagihanSiswaView: React.FC<TagihanSiswaViewProps> = ({
  tagihanList,
  siswaList,
  posList,
  kelasList,
  onGenerateTagihan,
  onDeleteTagihan,
  onNavigateToRecurring,
}) => {
  const { showAlert, showConfirm } = useDialog();
  const defaultSiswaId = siswaList.length > 0 ? siswaList[0].id : '';
  const [selectedSiswaId, setSelectedSiswaId] = useState(defaultSiswaId);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Modal generate state
  const [targetType, setTargetType] = useState<'SISWA' | 'KELAS' | 'ANGKATAN'>('SISWA');
  const [targetId, setTargetId] = useState(defaultSiswaId);
  const [targetPosId, setTargetPosId] = useState(posList.length > 0 ? posList[0].id : 'pos-2');
  const [tahunAjaran, setTahunAjaran] = useState('2026/2027');
  const [nominal, setNominal] = useState('200000');
  const [potongan, setPotongan] = useState('0');
  const [mulaiBulan, setMulaiBulan] = useState('Juli');

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filteredTagihan, setFilteredTagihan] = useState<TagihanSiswa[]>([]);
  const [sortBy, setSortBy] = useState('tanggalDibuat');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: string | keyof TagihanSiswa) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col as string);
      setSortDir('asc');
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.getTagihanPaginated({
          siswaId: selectedSiswaId === 'ALL' ? undefined : selectedSiswaId,
          page: currentPage,
          limit: rowsPerPage,
        });
        if (res.success) {
          setFilteredTagihan(res.data);
          setTotalPages(res.totalPages);
          setTotalRows(res.totalCount);
        }
      } catch (err) {
        console.error(err);
      }
    };
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [selectedSiswaId, search, currentPage, rowsPerPage]);

  const handleGenerateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onGenerateTagihan({
      targetType,
      targetId,
      posId: targetPosId,
      tahunAjaran,
      nominal: Number(nominal),
      potongan: Number(potongan),
      mulaiBulan,
    });
    setShowGenerateModal(false);
  };

  const selectedStudent = siswaList.find((s) => s.id === selectedSiswaId);

  const studentOptions = [
    { value: 'ALL', label: '-- Tampilkan Semua Siswa --', searchStr: 'all semua' },
    ...siswaList.map((s) => ({
      value: s.id,
      label: `${s.nis} - ${s.nama} (${s.kelasNama || 'Belum ada kelas'})`,
      searchStr: `${s.nis} ${s.nama} ${s.kelasNama || ''}`
    }))
  ];

  const modalStudentOptions = siswaList.map((s) => ({
      value: s.id,
      label: `${s.nis} - ${s.nama}`,
      searchStr: `${s.nis} ${s.nama}`
  }));

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header Matching Document Page 9 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">Tagihan Siswa</h2>
            <p className="text-xs text-slate-500">
              Pengelolaan kewajiban pembayaran SPP bulanan, cicilan pembangunan, dan tagihan tahunan siswa
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {onNavigateToRecurring && (
              <button
                onClick={onNavigateToRecurring}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition shadow-xs cursor-pointer"
                title="Buka Modul Recurring Billing SPP 12 Bulan Penuh"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>SPP Berkala Otomatis (1 Thn)</span>
              </button>
            )}
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Tagihan</span>
            </button>
            <button
              onClick={() => showAlert('Fitur massal update potongan/tarif tagihan siap digunakan di versi pro', { title: 'Fitur Pro' })}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-md border"
            >
              Ubah
            </button>
            <button
              onClick={() => showAlert('Fitur import excel tagihan', { title: 'Import Excel' })}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-md border"
            >
              Upload
            </button>
          </div>
        </div>

        {/* Filter Bar Exact from Page 9: Siswa Selector + Cari */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-600">Siswa:</span>
            <SearchableSelect
              options={studentOptions}
              value={selectedSiswaId}
              onChange={(val) => {
                setSelectedSiswaId(val);
                setTargetId(val);
              }}
              className="min-w-[320px]"
            />

            {selectedStudent && (
              <span className="text-xs bg-sky-100 text-sky-900 font-semibold px-2 py-1 rounded">
                VA: {selectedStudent.noVA}
              </span>
            )}
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search tagihan / bulan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-48"
            />
          </div>
        </div>
      </div>

      {/* Main Table Matching Page 9 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3 w-16 text-center">Aksi</th>
                <th className="py-2.5 px-2 w-10 text-center">No</th>
                <SortableHeader label="Siswa" columnKey="namaSiswa" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Tgl Entry" columnKey="tanggalDibuat" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nama Pos" columnKey="namaPos" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Bulan" columnKey="bulan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nominal Tagihan" columnKey="nominal" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Potongan/Diskon" columnKey="potongan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Total" columnKey="totalTagihan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Sudah Dibayar" columnKey="terbayar" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Sisa (Tunggakan)" columnKey="sisa" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Status Bayar" columnKey="status" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTagihan.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-8 text-slate-400">
                    Tidak ada data tagihan ditemukan
                  </td>
                </tr>
              ) : (
                filteredTagihan.map((t, idx) => (
                  <tr key={t.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {t.status === 'UNPAID' ? (
                          <button
                            onClick={async () => {
                              if (await showConfirm('Hapus tagihan ini?', { type: 'danger', title: 'Hapus Tagihan' })) onDeleteTagihan(t.id);
                            }}
                            className="px-1.5 py-0.5 text-[10px] font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 rounded border border-rose-300"
                          >
                            Del
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Terkunci</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center text-slate-500 font-mono text-[10px]">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td className="py-2 px-3">
                      <div className="font-bold text-slate-800">{t.namaSiswa}</div>
                      <div className="text-[10px] text-slate-500 font-mono">ID: {t.siswaId.slice(0, 5)}</div>
                    </td>
                    <td className="py-2 px-3 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                      {new Date(t.createdAt).toLocaleDateString('id-ID', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="py-2 px-3 font-semibold text-slate-800">
                      {t.namaPos} {t.tahunAjaran}
                    </td>
                    <td className="py-2 px-3 text-slate-600 font-bold">{t.bulan || '-'}</td>
                    <td className="py-2 px-3 text-right text-slate-600 font-mono">
                      {(t.nilai || 0).toLocaleString('id-ID')}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600 font-mono">
                      {(t.potongan || 0) > 0 ? (t.potongan || 0).toLocaleString('id-ID') : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-bold text-sky-800 font-mono">
                      {(t.total || 0).toLocaleString('id-ID')}
                    </td>
                    <td className="py-2 px-3 text-right font-bold text-emerald-600 font-mono">
                      {(t.bayar || 0) > 0 ? (t.bayar || 0).toLocaleString('id-ID') : '0'}
                    </td>
                    <td className="py-2 px-3 text-right font-bold text-rose-600 font-mono">
                      {(t.sisa || 0) > 0 ? (t.sisa || 0).toLocaleString('id-ID') : '0'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex justify-center">
                        {t.status === 'PAID' ? (
                          <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded flex items-center gap-1 text-[10px] whitespace-nowrap border border-emerald-200 shadow-[0_0_8px_rgba(16,185,129,0.2)]">
                            <CheckCircle2 className="w-3 h-3" /> LUNAS
                          </span>
                        ) : t.status === 'PARTIAL' ? (
                          <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded flex items-center gap-1 text-[10px] whitespace-nowrap border border-amber-200">
                            SEBAGIAN
                          </span>
                        ) : (
                          <span className="bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded flex items-center gap-1 text-[10px] whitespace-nowrap border border-rose-200">
                            <AlertCircle className="w-3 h-3" /> BELUM
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {/* Modal Tambah Tagihan (Exact Document Page 9 Middle) */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6 border border-slate-200 animate-in fade-in">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b">
              Tambah Tagihan Siswa (Massal / Individu)
            </h3>
            <form onSubmit={handleGenerateSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Target Pembuatan</label>
                  <select
                    value={targetType}
                    onChange={(e) => setTargetType(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="SISWA">Per Siswa</option>
                    <option value="KELAS">Satu Kelas (Massal)</option>
                    <option value="ANGKATAN">Satu Angkatan (Massal)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    {targetType === 'SISWA' ? 'Pilih Siswa' : targetType === 'KELAS' ? 'Pilih Kelas' : 'Tahun Angkatan'}
                  </label>
                  {targetType === 'SISWA' ? (
                    <SearchableSelect
                      options={modalStudentOptions}
                      value={targetId}
                      onChange={(val) => setTargetId(val)}
                      className="w-full"
                    />
                  ) : targetType === 'KELAS' ? (
                    <select
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md bg-white"
                    >
                      {kelasList.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.nama}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="2026"
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Pos Tagihan</label>
                  <select
                    value={targetPosId}
                    onChange={(e) => {
                      setTargetPosId(e.target.value);
                      const p = posList.find((x) => x.id === e.target.value);
                      if (p) setNominal(String(p.defaultNominal));
                    }}
                    className="w-full px-3 py-2 border rounded-md bg-white"
                  >
                    {posList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.kode} - {p.namaPos} ({p.jenis})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Tahun Ajaran</label>
                  <input
                    type="text"
                    value={tahunAjaran}
                    onChange={(e) => setTahunAjaran(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Nominal (Rp)</label>
                  <input
                    type="number"
                    value={nominal}
                    onChange={(e) => setNominal(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Potongan (Rp)</label>
                  <input
                    type="number"
                    value={potongan}
                    onChange={(e) => setPotongan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Mulai Bulan (Khusus SPP)</label>
                  <select
                    value={mulaiBulan}
                    onChange={(e) => setMulaiBulan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-white"
                  >
                    {['Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember', 'Januari'].map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md font-semibold"
                >
                  Generate Tagihan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
